import {createSelector, createStructuredSelector} from 'reselect';
import {create} from 'zustand';

import {TableEditableProps} from 'notebook/tables/core-ui/TableEditableProps';
import {TABLE_EDITABLE_DEFAULT_STATE, TableEditableState} from 'notebook/tables/core-ui/TableEditableState';
import {EvaluatedCell} from 'notebook/tables/excess/Evaluator';
import {Table} from 'notebook/tables/TableConstants';

/**
 * This holds volatile state, or state that frequently changes due to evaluation or renders.
 * Everything inside here will be deep-compare memoized.
 */
export interface TableEditableVolatileProps {
  table: Table;
  staleRows?: Array<number>;
  errorRows?: Array<boolean>;
  warningRows?: Array<boolean>;
  computedTable: Array<Array<EvaluatedCell>>;
}

export type TableEditableStableProps = Omit<TableEditableProps, keyof TableEditableVolatileProps>;

/**
 * This is the state in the Zustand store.  New non-volatile state can be added here.
 */
export interface TableEditableStore {
  stableProps: TableEditableStableProps;
  volatileProps: TableEditableVolatileProps;
  state: TableEditableState;
  setProps: (stableProps: TableEditableStableProps, volatileProps: TableEditableVolatileProps) => void;
  setState: (state: Partial<TableEditableState>) => void;
}

/**
 * This creates a new store for a structured table and holds all of the props and state for a TableEditable
 * component.  This is intended to be the sole source of truth for the inputs and state of TableEditable,
 * and all selectors and logic should derive models from this store.
 *
 * TableEditable's props are split into stable and volatile props.
 * All volatile props use a deep-compare selector to ensure that the component only re-renders when absolutely
 * necessary.  So any props that are very frequently changing should be placed here.
 *
 * All other props should go in stableProps, which are shallow-compare memoized.  All props in here should
 * not change object references unless they are actually changing.
 */
const tableStoreCreatorForType = create<TableEditableStore>();
type TableStoreHookType = ReturnType<typeof tableStoreCreatorForType>;
export function createTableEditableStore({
  stableProps,
  volatileProps,
}: {
  stableProps: TableEditableStableProps;
  volatileProps: TableEditableVolatileProps;
}): TableStoreHookType {
  return create<TableEditableStore>()(
    (set) =>
      ({
        stableProps,
        volatileProps,
        state: TABLE_EDITABLE_DEFAULT_STATE,
        setProps: (stableProps, volatileProps) => {
          set((store: TableEditableStore) => ({
            ...store,
            stableProps,
            volatileProps,
          }));
        },
        setState: (updatedState) => {
          set((store: TableEditableStore) => ({
            ...store,
            state: {
              ...store.state,
              ...updatedState,
            },
          }));
        },
      }) satisfies TableEditableStore
  );
}

export const createTableEditableStoreSelector = createSelector.withTypes<TableEditableStore>();
export const createTableEditableStoreStructuredSelector =
  createStructuredSelector.withTypes<TableEditableStore>();
